<script setup lang="ts">
  import { useMultiStepStore } from '@/components/forms/multi-step/store'
  import { useAQFV1 } from '@/components/forms/ask-question/flows/store'
  import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'
  import { FORMS_DISPLAY_DATE_FORMAT } from '@/utils/constants/date'
  import type { TPassengerFormData, TContactsDetails, TSubmitEmit } from '@/components/forms/ask-question/flows/types'
  import type { IFormSelector } from '@/components/types'

  const props = defineProps<IFormSelector>()
  const { t, locale } = useI18n()
  const dayjs = useDayjs()

  const translationBasePath = 'forms.askQuestions.addExtraHandBaggage.stepData'
  const multiStepStore = useMultiStepStore()
  multiStepStore.maxStep = 3
  multiStepStore.stepsData = [
    {
      title: t(`${translationBasePath}.passenger.progressTitle`),
      icon: 'common/outline-person',
      step: 1
    },
    {
      title: t(`${translationBasePath}.flight.progressTitle`),
      icon: 'common/airplane-45-deg',
      step: 2
    },
    {
      title: t(`${translationBasePath}.request.progressTitle`),
      icon: 'common/edit-document-outline',
      step: 3
    }
  ]

  const forceExitForm = ref(false)

  const globalSettingsStore = useGlobalSettingsStore()
  const backUrl = computed(() => {
    return globalSettingsStore.aqfBackLink?.href ?? '/'
  })

  // If booking details are not required, then show only multi step form
  multiStepStore.showMultiStepForm = multiStepStore.showMultiStepForm || !props.enterBookingDetails

  // If form was submitted successfully and page was reloaded, then exit form
  if (multiStepStore.isFormSubmittedSuccessfully) {
    forceExitForm.value = true
    exitForm()
  }

  const AQFV1Store = useAQFV1()
  AQFV1Store.initStates()

  function setDataFieldsPreview() {
    multiStepStore.dataFieldsPreview = [
      {
        data: AQFV1Store.passengerDetails
          ?.map((passenger) => (passenger.firstName ?? '') + (passenger.lastName ? ` ${passenger.lastName}` : ''))
          .filter((passenger) => passenger),
        title: t('passengers'),
        step: 1
      },
      {
        data: AQFV1Store.flightDetails
          ?.map(
            (flight) =>
              (flight.flightDate ? dayjs(flight.flightDate).format(FORMS_DISPLAY_DATE_FORMAT) : '') +
              (flight.flightNumber ? ` • ${flight.flightNumber}` : '') +
              (flight.origin ? ` • ${flight.origin}` : '') +
              (flight.destination ? ` • ${flight.destination}` : '')
          )
          .filter((flight) => flight),
        title: t('flights'),
        step: 2
      }
    ].filter((field) => multiStepStore.currentStep > field.step && field.data)
  }

  watch(
    () => multiStepStore.currentStep,
    (newStep) => {
      if (newStep < 2) {
        return
      }

      setDataFieldsPreview()
    }
  )

  const isOpen = ref(false)

  async function lastStepSubmit(
    proceedToNextCallback: (callback: () => void, isValid: boolean) => void,
    data: TSubmitEmit<TContactsDetails>
  ) {
    proceedToNextCallback(() => (AQFV1Store.contactsDetails = data.formData), data.isValid)

    if (!data.isValid) {
      return
    }

    const response = await $fetch('/api/forms/ask-question/submit', {
      method: 'POST',
      body: {
        language: locale.value.toUpperCase(),
        pnr: AQFV1Store.pnr || '',
        type: props.internalEmailType ?? 'internal',
        category: props.category || '',
        subCategory: props.subCategory || '',
        subSubCategory: props.subSubCategory || '',
        passengers: AQFV1Store.passengerDetails,
        flightInfo: AQFV1Store.flightDetails,
        contacts: AQFV1Store.contactsDetails,
        message: AQFV1Store.contactsDetails?.message
      }
    })

    multiStepStore.isFormSubmittedSuccessfully = response.success
    isOpen.value = true
  }

  async function exitForm(resetFormState: boolean = true) {
    if (!resetFormState) {
      return
    }

    multiStepStore.showLoading = true
    forceExitForm.value = true

    await navigateTo({ path: backUrl.value })
  }

  watch(
    () => multiStepStore.showMultiStepForm,
    async (newVal) => {
      if (forceExitForm.value) {
        return
      }

      if (!newVal) {
        AQFV1Store.resetStates()
        // Needs to be reset, because form data will be still filled if user returns to starting point
        if (!props.enterBookingDetails) {
          await navigateTo({ path: backUrl.value })
        }
      }
    }
  )

  // Resets data only when router is changing after submit
  onBeforeRouteUpdate(() => {
    if (forceExitForm.value) {
      multiStepStore.resetStates()
      AQFV1Store.resetStates()
    }
  })
</script>
<template>
  <div>
    <LazyFormsEnterBookingDetailsV1 v-if="props.enterBookingDetails && !multiStepStore.showMultiStepForm" />
    <FormsLayoutDefault v-else>
      <template #forms="{ proceedToNextStep }">
        <LazyFormsPassengerDetailsV1
          v-if="multiStepStore.currentStep === 1"
          :trigger-submit="multiStepStore.triggerSubmit"
          :data="AQFV1Store.passengerDetails as TPassengerFormData[]"
          @update="(data) => (AQFV1Store.passengerDetails = data)"
          @submit="(data) => proceedToNextStep(() => (AQFV1Store.passengerDetails = data.formData), data.isValid)"
        />
        <LazyFormsFlightDetailsV1
          v-else-if="multiStepStore.currentStep === 2"
          :trigger-submit="multiStepStore.triggerSubmit"
          :data="AQFV1Store.flightDetails"
          @update="(data) => (AQFV1Store.flightDetails = data)"
          @submit="(data) => proceedToNextStep(() => (AQFV1Store.flightDetails = data.formData), data.isValid)"
        />
        <LazyFormsContactDetailsV1
          v-else-if="multiStepStore.currentStep === 3"
          :trigger-submit="multiStepStore.triggerSubmit"
          :data="AQFV1Store.contactsDetails as TContactsDetails"
          @update="(data) => (AQFV1Store.contactsDetails = data)"
          @submit="(data) => lastStepSubmit(proceedToNextStep, data)"
        />
      </template>
    </FormsLayoutDefault>
    <LazyUiModalNotification
      v-model="isOpen"
      :is-valid="multiStepStore.isFormSubmittedSuccessfully"
      @update:model-value="(val: boolean) => !val && exitForm()"
    >
      <template #title>
        {{
          multiStepStore.isFormSubmittedSuccessfully
            ? $t('forms.askQuestions.modal.success.title')
            : $t('forms.global.error.somethingWentWrong')
        }}
      </template>
      <template #description>
        {{
          multiStepStore.isFormSubmittedSuccessfully
            ? $t('forms.askQuestions.modal.success.description')
            : $t('forms.askQuestions.modal.fail.description')
        }}
      </template>
      <template #button>
        <UiButton
          :title="$t('continue')"
          variant="secondary"
          size="lg"
          class="mt-8 w-full md:w-fit"
          @click="exitForm()"
        />
      </template>
    </LazyUiModalNotification>
  </div>
</template>
